import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getMedicalRecords,
  getSessionById,
  getSessionMeetingInfo,
  createSessionCalendarEvent,
  getSessionAttendance,
  refreshSessionAttendance,
  getSessionRecordings,
  refreshSessionRecordings,
  updateClinicianSession,
  processRefund,
  getRefundStatus,
} from "../../../api/services/api";
import Loader from "../../../components/Loader";
import textCapitalization from "../../../components/textCapitalization";
import {
  getHandoverFlags,
  getPayTabs,
  getPrimarySessionDate,
  getSessionTimeslots,
  getSessionClinicianParty,
  getSessionPatientParties,
  getConsultationMode,
  getSessionPayment,
  isVirtualConsultation,
} from "../../../utils/sessionFields";
import { ChangeClinician } from "./ChangeClinician";
import { SessionDetailCard } from "./SessionDetailCard";
import { RefundDialog, RefundData } from "./RefundDialog";
import { RefundStatusView } from "./RefundStatusView";

export const SessionView = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = React.useState(-1);
  const [patientProfileId, setpatientProfileId] = useState("");
  console.log(patientProfileId, "Check One");
  const [openDialog, setOpenDialog] = useState(false);
  const [openClinicians, setOpenClinicians] = useState(false);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [openRefundStatus, setOpenRefundStatus] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const { data, isLoading } = useQuery(["sessions", id], getSessionById);
  const session = data?.data;
  const handoverFlags = session ? getHandoverFlags(session) : null;
  const payTabs = session ? getPayTabs(session) : null;
  const payment = session ? getSessionPayment(session) : null;
  const sessionTimeslots = session ? getSessionTimeslots(session) : [];
  const sessionDate = session ? getPrimarySessionDate(session) : null;
  const patientParties = session ? getSessionPatientParties(session) : null;
  const clinicianParty = session ? getSessionClinicianParty(session) : null;
  const patientAddress =
    session?.patientAddress ??
    session?.patient_address ??
    session?.addresses ??
    null;
  const patientProfile = patientParties?.profile ?? null;

  const { data: meetingInfoData, refetch: refetchMeetingInfo } = useQuery(
    ["session-meeting-info", session?.id],
    getSessionMeetingInfo,
    {
      enabled: Boolean(session?.id) && isVirtualConsultation(session),
    }
  );

  const { mutate: refreshCalendar, isLoading: isCalendarRefreshing } =
    useMutation(createSessionCalendarEvent, {
      onSuccess: () => {
        refetchMeetingInfo();
        toast.success("Calendar event refreshed");
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to refresh calendar event"
        );
      },
    });

  const { data: attendanceData, refetch: refetchAttendance } = useQuery(
    ["session-attendance", session?.id],
    getSessionAttendance,
    {
      enabled: Boolean(session?.id) && isVirtualConsultation(session),
    }
  );
  const attendance = attendanceData?.data;

  const { mutate: refreshAttendance, isLoading: isAttendanceRefreshing } =
    useMutation(refreshSessionAttendance, {
      onSuccess: () => {
        refetchAttendance();
        toast.success("Attendance refreshed from Google Meet");
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to refresh attendance"
        );
      },
    });

  const { data: recordingsData, refetch: refetchRecordings } = useQuery(
    ["session-recordings", session?.id],
    getSessionRecordings,
    {
      enabled: Boolean(session?.id) && isVirtualConsultation(session),
    }
  );
  const recordings = recordingsData?.data;

  const { mutate: refreshRecordings, isLoading: isRecordingsRefreshing } =
    useMutation(refreshSessionRecordings, {
      onSuccess: () => {
        refetchRecordings();
        toast.success("Recording fetch triggered — may take a few minutes if Google is still processing");
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to refresh recordings"
        );
      },
    });

  const recordingStatusLabel = (status: string) => {
    switch (status) {
      case "READY":
        return "Saved";
      case "PROCESSING":
        return "Downloading";
      case "PENDING":
        return "Waiting for Google";
      case "NO_RECORDING":
        return "Not found";
      case "FAILED":
        return "Failed";
      default:
        return "Pending";
    }
  };

  const recordingStatusColor = (status: string) => {
    switch (status) {
      case "READY":
        return "success";
      case "PROCESSING":
      case "PENDING":
        return "warning";
      case "NO_RECORDING":
      case "FAILED":
        return "error";
      default:
        return "default";
    }
  };

  const { data: getMedicalRecordsData } = useQuery(
    [
      "medical-record",
      {
        patientProfileId: patientProfileId,
      },
    ],
    getMedicalRecords
  );
  console.log(getMedicalRecordsData, "Medical");

  const { data: refundStatusData, refetch: refetchRefundStatus } = useQuery(
    ["refund-status", data?.data?.id],
    getRefundStatus,
    {
      enabled: !!data?.data?.id,
    }
  );

  const { mutate } = useMutation(updateClinicianSession, {
    onSuccess: (res) => {
      console.log(res);
      queryClient.invalidateQueries("sessions");
      toast.success("Clinician Updated");
    },
    onError: (res: any) => {
      console.log(res);
      toast.error(res?.response?.data?.message);
    },
  });

  const { mutate: refundMutate, isLoading: isRefundLoading } = useMutation(
    processRefund,
    {
      onSuccess: (res) => {
        console.log(res);
        queryClient.invalidateQueries("sessions");
        refetchRefundStatus();
        toast.success("Refund processed successfully");
        setOpenRefundDialog(false);
      },
      onError: (res: any) => {
        console.log(res);
        toast.error(res?.response?.data?.message || "Failed to process refund");
      },
    }
  );

  const currentDate = moment(new Date()).format("YYYY-MM-DD HH:mm:ss");

  const handleChangeClinician = (newClinicianID: any) => {
    const scId = handoverFlags?.sessionClinicianId;
    if (!scId) {
      toast.error("Cannot assign clinician: session clinician record missing");
      return;
    }
    const res = {
      sessionId: session?.id,
      previousClinicianId: session?.clinician?.id,
      newClinicianId: newClinicianID,
      currentDate: currentDate,
    };
    mutate({ id: scId, body: res });
    setOpenClinicians(false);
  };

  const handleReports = (id: any) => {
    console.log(id, "This is ID");
    setpatientProfileId(id);
    setOpenDialog(true);
  };

  const handleRefund = (refundData: RefundData) => {
    refundMutate({
      sessionId: data?.data?.id,
      body: {
        reason: refundData.reason,
        amount:
          refundData.refundType === "partial" ? refundData.amount : undefined,
      },
    });
  };

  const canProcessRefund = () => {
    if (!session) return false;

    const paymentSuccessful = payTabs?.paymentStatus === true;

    const noRefundProcessed =
      !payTabs?.refundStatus || payTabs?.refundStatus === "NONE";

    const allowedStatuses = ["PAID", "CANCELLED", "COMPLETED"];
    const statusAllowed = allowedStatuses.includes(session.status);

    return paymentSuccessful && noRefundProcessed && statusAllowed;
  };

  const openMenu = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClinicianClick = (event: React.MouseEvent<HTMLElement>) => {
    if (data?.data?.status !== "PENDING") {
      setAnchorEl(event.currentTarget);
    } else {
      toast.error("Clinician cannot be selected due to Pending status");
    }
  };

  const handleMenu = () => {
    setOpenClinicians(true);
    setAnchorEl(null);
  };

  useEffect(() => {
    const sessionArray = data?.data?.sessionStatuses;
    if (sessionArray) {
      const lastObject = sessionArray[sessionArray?.length - 1];
      const index = sessionArray?.indexOf(lastObject);
      console.log(index);
      setActiveStep(index);
    }
  }, [data?.data?.sessionStatuses]);

  const consultationMode = session ? getConsultationMode(session) : "";
  let mode = consultationMode
    ? consultationMode.slice(0, 1).toUpperCase() +
      consultationMode.slice(1).toLowerCase()
    : "";

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", pb: 4 }}>
      <Box
        display="flex"
        gap={2}
        alignItems="center"
        sx={{
          backgroundColor: "#fff",
          px: 3,
          py: 2.5,
          boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
          mb: 3,
        }}
      >
        <IconButton
          onClick={() => navigate("/sessions")}
          sx={{
            backgroundColor: theme.colors.primary.lighter,
            "&:hover": { backgroundColor: theme.colors.primary.light },
          }}
        >
          <ArrowBackIcon sx={{ color: theme.colors.primary.main }} />
        </IconButton>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            Session Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.data?.sessionId
              ? `Session ID: ${data?.data?.sessionId}`
              : ""}
          </Typography>
        </Box>
      </Box>

      <Box px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%)`,
                  px: 3,
                  py: 2.5,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "#fff", fontWeight: 600 }}
                >
                  Session Information
                </Typography>
              </Box>
              <Divider />
              {isLoading ? (
                <Loader />
              ) : (
                <SessionDetailCard
                  key={id}
                  sessionId={session?.sessionId?.toString() || ""}
                  speciality={session?.service?.title || ""}
                  modeOfConsultation={mode}
                  bookedDate={moment(
                    session?.createdAt ?? session?.created_at
                  ).format("DD-MM-YYYY")}
                  sessionDate={moment(sessionDate).format("DD")}
                  sessionMonth={moment(sessionDate).format("MMM")}
                  timeSlots={sessionTimeslots}
                  symptoms={session?.symptom || "N/A"}
                  description={session?.description || "N/A"}
                  doctorImage={clinicianParty?.imageUrl || ""}
                  doctorName={clinicianParty?.name || ""}
                  doctorExperience={clinicianParty?.experience || ""}
                  doctorSpeciality={clinicianParty?.designation || ""}
                  doctorEmail={clinicianParty?.email || "N/A"}
                  doctorMobile={clinicianParty?.mobile || "N/A"}
                  doctorLicense={clinicianParty?.license || ""}
                  doctorLocation={clinicianParty?.location || ""}
                  doctorLanguages={clinicianParty?.languages || ""}
                  newClinician={clinicianParty?.newClinician}
                  patientImage={patientParties?.accountHolderImage || ""}
                  patientName={patientParties?.accountHolderName || ""}
                  patientNumber={patientParties?.accountHolderMobile || "N/A"}
                  patientEmail={patientParties?.accountHolderEmail || "N/A"}
                  patientProfileImage={patientParties?.profileImage || ""}
                  PatientProfileName={patientParties?.profileName || ""}
                  PatientProfileAge={patientParties?.profileAge || 0}
                  PatientProfileGender={patientParties?.profileGender || ""}
                  PatientProfileRelation={patientParties?.profileRelation || ""}
                  PatientLandmark={patientAddress?.landmark || ""}
                  patientAddress1={
                    patientAddress?.addressLine1 ||
                    patientAddress?.address_line1 ||
                    "N/A"
                  }
                  patientAddress2={
                    patientAddress?.addressLine2 ||
                    patientAddress?.address_line2 ||
                    "N/A"
                  }
                  PatientCity={patientAddress?.city || ""}
                  consultationFee={payment?.amount || 0}
                  paymentDetails={payment}
                  payTabsDetails={payTabs}
                  handleReportClick={() =>
                    handleReports(patientProfile?.id)
                  }
                  clinicianClick={handleClinicianClick}
                  clinicianData={session}
                  handoverFlags={handoverFlags ?? undefined}
                  onRefundClick={() => setOpenRefundDialog(true)}
                  onViewRefundStatus={() => setOpenRefundStatus(true)}
                  canProcessRefund={canProcessRefund()}
                  refundStatus={payTabs?.refundStatus}
                />
              )}
            </Card>
          </Grid>
          <Grid item xs={12} lg={4}>
            {session && isVirtualConsultation(session) && meetingInfoData?.data ? (
              <Card
                sx={{
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${theme.colors.info.main || "#1976d2"} 0%, ${theme.colors.info.dark || "#1565c0"} 100%)`,
                    px: 3,
                    py: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography variant="h5" sx={{ color: "#fff", fontWeight: 600 }}>
                    Meeting Info
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isCalendarRefreshing}
                    onClick={() => session?.id && refreshCalendar(session.id)}
                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
                  >
                    Refresh Calendar
                  </Button>
                </Box>
                <Divider />
                <Box px={3} py={2} display="flex" flexDirection="column" gap={1}>
                  {meetingInfoData.data.meetingInfo?.timeslots?.length ? (
                    meetingInfoData.data.meetingInfo.timeslots.map(
                      (slot: any, idx: number) => (
                        <Box key={idx}>
                          <Typography variant="body2">
                            <strong>Part {slot.partNumber ?? slot.part_number}:</strong>{" "}
                            {slot.meetingLink ??
                              slot.meeting_link ??
                              slot.eventLink ??
                              slot.event_link ??
                              "No link yet"}
                          </Typography>
                        </Box>
                      )
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No meeting links configured yet
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Calendar events:{" "}
                    {meetingInfoData.data.meetingInfo?.hasCalendarEvent
                      ? "Created"
                      : "Not created"}
                  </Typography>
                </Box>
              </Card>
            ) : null}
            {session && isVirtualConsultation(session) ? (
              <Card
                sx={{
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${
                      theme.colors.success.main || "#2e7d32"
                    } 0%, ${theme.colors.success.dark || "#1b5e20"} 100%)`,
                    px: 3,
                    py: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "#fff", fontWeight: 600 }}
                  >
                    Meet Attendance
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isAttendanceRefreshing}
                    onClick={() => session?.id && refreshAttendance(session.id)}
                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
                  >
                    {isAttendanceRefreshing ? "Refreshing..." : "Refresh"}
                  </Button>
                </Box>
                <Divider />
                <Box px={3} py={2} display="flex" flexDirection="column" gap={1.5}>
                  {attendance?.configured === false ? (
                    <Typography variant="body2" color="text.secondary">
                      Meet attendance is not configured on the server (requires
                      Google Workspace + Meet API access).
                    </Typography>
                  ) : (
                    <>
                      {attendance?.inProgress ? (
                        <Typography variant="caption" color="warning.main">
                          Session still in progress or Meet not fully closed — totals
                          may update on the next refresh.
                        </Typography>
                      ) : null}
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`Clinician: ${
                            attendance?.clinicianAttended
                              ? `${attendance?.inProgress ? "Partial · " : "Attended · "}${attendance?.clinicianTotalDurationFormatted ?? "—"}${
                                  (attendance?.clinicianJoinCount ?? 0) > 1
                                    ? ` (${attendance.clinicianJoinCount} joins)`
                                    : ""
                                }`
                              : attendance?.checked
                              ? "No show"
                              : "Pending"
                          }`}
                          color={
                            attendance?.clinicianAttended
                              ? "success"
                              : attendance?.checked
                              ? "error"
                              : "default"
                          }
                          variant={
                            attendance?.clinicianAttended ? "filled" : "outlined"
                          }
                        />
                        <Chip
                          size="small"
                          label={`Patient (clinical): ${
                            attendance?.patientProfileAttended ?? attendance?.patientAttended
                              ? `${attendance?.inProgress ? "Partial · " : "Attended · "}${attendance?.patientTotalDurationFormatted ?? "—"}${
                                  (attendance?.patientJoinCount ?? 0) > 1
                                    ? ` (${attendance.patientJoinCount} joins)`
                                    : ""
                                }`
                              : attendance?.checked
                              ? "No show"
                              : "Pending"
                          }`}
                          color={
                            (attendance?.patientProfileAttended ?? attendance?.patientAttended)
                              ? "success"
                              : attendance?.checked
                              ? "error"
                              : "default"
                          }
                          variant={
                            (attendance?.patientProfileAttended ?? attendance?.patientAttended)
                              ? "filled"
                              : "outlined"
                          }
                        />
                        <Chip
                          size="small"
                          label={`Account holder: ${
                            attendance?.accountHolderAttended
                              ? `${attendance?.inProgress ? "Partial · " : "Attended · "}${attendance?.accountHolderTotalDurationFormatted ?? "—"}${
                                  (attendance?.accountHolderJoinCount ?? 0) > 1
                                    ? ` (${attendance.accountHolderJoinCount} joins)`
                                    : ""
                                }`
                              : attendance?.checked
                              ? "No show"
                              : "Pending"
                          }`}
                          color={
                            attendance?.accountHolderAttended
                              ? "info"
                              : attendance?.checked
                              ? "default"
                              : "default"
                          }
                          variant={
                            attendance?.accountHolderAttended ? "filled" : "outlined"
                          }
                        />
                      </Box>

                      {attendance?.participants?.length ? (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Cumulative time (all join/leave segments)
                          </Typography>
                          {attendance.participants.map((p: any, idx: number) => (
                            <Box
                              key={`${p.timeslotId}-${p.participantId ?? p.name ?? idx}`}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{ mt: 0.5 }}
                            >
                              <Typography variant="body2">
                                {p.name || "Unknown"}{" "}
                                {p.email ? (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({p.email})
                                  </Typography>
                                ) : null}
                                {p.party && p.party !== "UNKNOWN" ? (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({textCapitalization(p.party.replace(/_/g, " "))}
                                    {p.matchMethod ? ` · ${p.matchMethod}` : ""})
                                  </Typography>
                                ) : p.role && p.role !== "UNKNOWN" ? (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ({textCapitalization(p.role)})
                                  </Typography>
                                ) : null}
                                {(p.joinCount ?? 0) > 1 ? (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {" "}
                                    · {p.joinCount} joins
                                  </Typography>
                                ) : null}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {p.totalDurationFormatted ??
                                  (p.durationSeconds != null
                                    ? `${Math.round(p.durationSeconds / 60)} min`
                                    : "—")}
                              </Typography>
                            </Box>
                          ))}

                          {attendance?.segments?.length > 1 ? (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                              >
                                Individual segments
                              </Typography>
                              {attendance.segments.map((s: any) => (
                                <Box
                                  key={s.id}
                                  display="flex"
                                  justifyContent="space-between"
                                  sx={{ mt: 0.25 }}
                                >
                                  <Typography variant="caption" color="text.secondary">
                                    {s.name || "Unknown"}
                                    {s.email ? ` · ${s.email}` : ""}
                                    {s.party && s.party !== "UNKNOWN"
                                      ? ` (${textCapitalization(s.party.replace(/_/g, " "))})`
                                      : s.role && s.role !== "UNKNOWN"
                                      ? ` (${textCapitalization(s.role)})`
                                      : ""}
                                    {s.joinTime
                                      ? ` · ${moment(s.joinTime).format("HH:mm")}`
                                      : ""}
                                    {s.leaveTime
                                      ? `–${moment(s.leaveTime).format("HH:mm")}`
                                      : ""}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {s.durationSeconds != null
                                      ? `${Math.round(s.durationSeconds / 60)} min`
                                      : "—"}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : null}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {attendance?.checked
                            ? "No participants were recorded for this session."
                            : "Attendance not reconciled yet. It is checked automatically after the session ends, or use Refresh."}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Card>
            ) : null}
            {session && isVirtualConsultation(session) ? (
              <Card
                sx={{
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${
                      theme.colors.primary.main || "#1565c0"
                    } 0%, ${theme.colors.primary.dark || "#0d47a1"} 100%)`,
                    px: 3,
                    py: 2.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "#fff", fontWeight: 600 }}
                  >
                    Meet Recordings
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isRecordingsRefreshing}
                    onClick={() => session?.id && refreshRecordings(session.id)}
                    sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
                  >
                    {isRecordingsRefreshing ? "Fetching..." : "Fetch recording"}
                  </Button>
                </Box>
                <Divider />
                <Box px={3} py={2} display="flex" flexDirection="column" gap={1.5}>
                  {recordings?.configured === false ? (
                    <Typography variant="body2" color="text.secondary">
                      Meet recording pipeline is not configured on the server
                      (requires Google Workspace + Meet/Drive API access).
                    </Typography>
                  ) : !recordings?.totalMeetTimeslots ? (
                    <Typography variant="body2" color="text.secondary">
                      No Meet timeslots for this session.
                    </Typography>
                  ) : (
                    <>
                      {recordings?.pendingCount > 0 && !recordings?.allReady ? (
                        <Typography variant="caption" color="warning.main">
                          Google often needs 15–60 minutes after the session to
                          generate the recording file. The server retries automatically.
                        </Typography>
                      ) : null}
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`${recordings?.readyCount ?? 0} / ${recordings?.totalMeetTimeslots ?? 0} saved`}
                          color={recordings?.allReady ? "success" : "default"}
                          variant={recordings?.allReady ? "filled" : "outlined"}
                        />
                      </Box>
                      {recordings?.timeslots?.map((t: any) => (
                        <Box
                          key={t.id}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={1}
                        >
                          <Box>
                            <Typography variant="body2">
                              {t.partNumber
                                ? `Part ${t.partNumber} · `
                                : ""}
                              {moment(t.date).format("MMM D, YYYY")}
                            </Typography>
                            {t.checkedAt ? (
                              <Typography variant="caption" color="text.secondary">
                                Last checked {moment(t.checkedAt).fromNow()}
                              </Typography>
                            ) : null}
                          </Box>
                          <Box display="flex" gap={1} alignItems="center">
                            <Chip
                              size="small"
                              label={recordingStatusLabel(t.status)}
                              color={recordingStatusColor(t.status) as any}
                              variant={
                                t.status === "READY" ? "filled" : "outlined"
                              }
                            />
                            {t.recordingUrl ? (
                              <Button
                                size="small"
                                href={t.recordingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<DownloadIcon />}
                              >
                                Open
                              </Button>
                            ) : null}
                          </Box>
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              </Card>
            ) : null}
            <Card
              sx={{
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  background: `linear-gradient(135deg, ${
                    theme.colors.secondary.main || "#f97316"
                  } 0%, ${theme.colors.secondary.dark || "#ea580c"} 100%)`,
                  px: 3,
                  py: 2.5,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "#fff", fontWeight: 600 }}
                >
                  Timeline & Status
                </Typography>
              </Box>
              <Divider />
              <Box
                overflow="auto"
                maxHeight="800px"
                className="scrollBox"
                px={3}
                py={2}
              >
                <Stepper
                  sx={{ background: "transparent" }}
                  activeStep={activeStep}
                  orientation="vertical"
                >
                  {data?.data?.sessionStatuses?.map(
                    (step: any, index: number) => (
                      <Step key={index} completed={true}>
                        <StepLabel
                          sx={{
                            "& .MuiStepLabel-iconContainer": {
                              "& .Mui-completed": {
                                color: theme.colors.success.main,
                              },
                              "& .Mui-active": {
                                color: theme.colors.primary.main,
                              },
                            },
                          }}
                        >
                          <Card
                            sx={{
                              ml: 1,
                              px: 2.5,
                              py: 2,
                              boxShadow:
                                index === activeStep
                                  ? `0 4px 12px ${theme.colors.primary.main}40`
                                  : "0px 2px 8px rgba(0,0,0,0.08)",
                              border:
                                index === activeStep
                                  ? `2px solid ${theme.colors.primary.main}`
                                  : "1px solid rgba(0,0,0,0.08)",
                              borderRadius: "8px",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                              },
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                color:
                                  index === activeStep
                                    ? theme.colors.primary.main
                                    : "text.primary",
                              }}
                            >
                              {textCapitalization(
                                step.status.replace(/_/g, " ")
                              )}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {moment(step.createdAt).format("DD MMM YYYY")}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {moment(step.createdAt).format("hh:mm A")}
                            </Typography>
                          </Card>
                        </StepLabel>
                      </Step>
                    )
                  )}
                </Stepper>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle sx={{ fontSize: "20px" }}>Medical Records</DialogTitle>
        <DialogContent sx={{ maxWidth: "550px" }} dividers>
          <Box width="100%" height="100%">
            {getMedicalRecordsData?.data?.data?.length === 0 ? (
              <Typography variant="h5" sx={{ opacity: "0.7" }}>
                No Medical Record Found...{" "}
              </Typography>
            ) : (
              <>
                {getMedicalRecordsData?.data?.data?.map((reports: any) => (
                  <>
                    <Box>
                      <Box maxHeight="500px">
                        <List sx={{ width: "500px", px: 1 }}>
                          <ListItem
                            secondaryAction={
                              <Box>
                                <a
                                  href={reports?.fileUrl}
                                  download
                                  style={{ textDecoration: "none" }}
                                >
                                  <Button variant="outlined">
                                    <DownloadIcon sx={{ pr: 1 }} />
                                    Download Report
                                  </Button>
                                </a>
                              </Box>
                            }
                          >
                            <Box
                              sx={{
                                backgroundColor: theme.colors.primary.lighter,
                                p: 1.5,
                                borderRadius: "30px",
                                display: "flex",
                                justifyContent: "center",
                                mr: 1,
                              }}
                            >
                              <Icon sx={{ color: theme.colors.primary.main }}>
                                <DescriptionOutlinedIcon />
                              </Icon>
                            </Box>
                            <ListItemText>Report {reports.id}</ListItemText>
                          </ListItem>
                        </List>
                      </Box>
                    </Box>
                  </>
                ))}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <Menu
        open={openMenu}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      >
        <MenuItem onClick={handleMenu} sx={{ width: "150px" }}>
          <Typography>Change Clinician</Typography>
        </MenuItem>
      </Menu>

      <ChangeClinician
        sessionData={data?.data}
        handleChangeClinician={(e: any) => handleChangeClinician(e)}
        openClinicians={openClinicians}
        setOpenClinicians={setOpenClinicians}
      />

      <RefundDialog
        open={openRefundDialog}
        onClose={() => setOpenRefundDialog(false)}
        onConfirm={handleRefund}
        sessionData={data?.data}
        loading={isRefundLoading}
      />

      <Dialog
        open={openRefundStatus}
        onClose={() => setOpenRefundStatus(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Refund History</DialogTitle>
        <DialogContent>
          <RefundStatusView
            refunds={
              (Array.isArray(refundStatusData?.data)
                ? refundStatusData?.data
                : []) as any[]
            }
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};
